import { useContext, useState } from 'react';
import NumberFormat from 'react-number-format';
import { APIBatchOperation } from '../../../../../../typings/BatchOperation.interface';
import { GlobalContext } from '../../contexts/GlobalContext';
import Button from '../base/Button';
import ProgressBar from '../base/ProgressBar';
import Text from '../base/Text';
import { Div } from '../helpers/StyledUtils';
import { useToasts } from '../Toast';
import { BULK_OPERATION_MODEL_NAVIGATION, BulkOperationModel, BulkOperationType } from './types';
import { BULK_OPERATION_CONFIGS, calculateProgressPercentage, getOperationLabels } from './utils';
interface BulkOperationProgressProps {
  operation: APIBatchOperation;
  operation_type: BulkOperationType;
  model?: BulkOperationModel;
  onCancel?: (operation: APIBatchOperation) => void;
  mutate?: () => void;
}
const BulkOperationProgress: React.FC<BulkOperationProgressProps> = ({
  operation,
  operation_type,
  model,
  onCancel,
  mutate
}) => {
  const {
    HookdeckAPI
  } = useContext(GlobalContext);
  const {
    addToast
  } = useToasts();
  const [pending, setPending] = useState(false);
  const is_finished = !!operation.completed_at || !!operation.cancelled_at;
  const config = BULK_OPERATION_CONFIGS[operation_type];
  const labels = getOperationLabels(operation_type, model);

  // Calculate progress based on operation type
  const progress = config.progress_calculation === 'percentage' && operation.estimated_count ? calculateProgressPercentage(operation.completed_count || 0, operation.estimated_count) : Math.floor((operation.progress || 0) * 100);

  // Get navigation path for the operation
  const navigation_path = {
    pathname: BULK_OPERATION_MODEL_NAVIGATION[model || 'events'].path || '/events',
    search: `?bulk_retry_id=${operation.id}`
  };
  const handleCancel = async () => {
    setPending(true);
    if (pending) return;
    try {
      const api_method = operation_type === 'cancel' ? 'cancelEvents' : model || 'events';
      if (api_method === 'cancelEvents') {
        await HookdeckAPI.bulk.events.cancel(operation.id);
        mutate?.();
        addToast('success', `Aborted bulk ${operation_type} #${operation.number}`);
      } else {
        const cancelledOperation = await HookdeckAPI.bulk[api_method].cancel(operation.id);
        onCancel?.(cancelledOperation);
        addToast('success', `Aborted bulk ${operation_type} #${operation.number}`);
      }
    } catch (e) {
      addToast('error', `Failed to cancel bulk ${operation_type} #${operation.number}`);
    }
    setPending(false);
  };

  // Render progress section if not finished
  const renderProgressSection = () => {
    if (is_finished) return null;
    return <>
        <Div flex={{
        justify: 'space-between'
      }}>
          <Text size="s">
            {operation.cancelled_at ? 'Aborted' : `${operation_type === 'cancel' ? 'Cancel' : 'Retry'} progress`}
          </Text>
          <Text size="s">{progress}%</Text>
        </Div>
        <ProgressBar m={{
        t: 3,
        b: 4
      }} value={progress} danger={!!operation.cancelled_at} />
      </>;
  };
  return <Div m={{
    t: 4
  }}>
      {renderProgressSection()}
      <Div flex={{
      justify: 'space-between',
      align: 'center'
    }}>
        <Text size="s" bold>
          <NumberFormat renderText={v => v} displayType="text" value={operation.completed_count || 0} thousandSeparator={','} />{' '}
          {is_finished ? `${operation_type === 'cancel' ? 'events cancelled' : labels.model_label + ' retried'}` : labels.model_label}
        </Text>
        <Div flex={{
        justify: 'flex-end'
      }}>
          {operation.in_progress && <Button disabled={pending} onClick={handleCancel} outline icon="block">
              {labels.abort_label}
            </Button>}
          <Button outline m={{
          l: 2
        }} to={navigation_path} icon="link">
            {labels.view_label}
          </Button>
        </Div>
      </Div>
    </Div>;
};
export default BulkOperationProgress;